import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function About() {
  const values = [
    {
      title: 'Contextual Design',
      description: 'Every project responds to its unique environment, culture, and community, creating architecture that belongs to its place.'
    },
    {
      title: 'Sustainable Practice',
      description: 'We integrate environmental responsibility into every decision, from material selection to energy performance.'
    },
    {
      title: 'Collaborative Process',
      description: 'Great architecture emerges from genuine dialogue. We partner closely with clients, consultants, and craftspeople.'
    },
    {
      title: 'Timeless Quality',
      description: 'We design buildings that age gracefully, prioritizing enduring materials and proportions over fleeting trends.'
    }
  ];

  const team = [
    {
      name: 'Ar. Chirag Manav',
      role: 'Co-Founder & Principal Architect',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
    },
    {
      name: 'Ar. Shruti Dhanda',
      role: 'Co-Founder & Design Director',
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
    }
  ];

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="about-hero">
        <div className="max-w-4xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block"
          >
            About Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-stone-900 leading-tight mb-8"
          >
            We shape spaces that
            <br />
            <span className="italic">shape lives</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-dm-sans text-lg text-stone-500 leading-relaxed max-w-2xl"
          >
            A boutique architecture and interior design practice led by Ar. Chirag Manav and Ar. Shruti Dhanda, BIOM is driven by a vision to create spaces that are both evocative and enduring.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-dm-sans text-lg text-stone-500 leading-relaxed max-w-2xl mt-6"
          >
            With a design approach rooted in tradition, innovation and deep personalization, the studio is known for crafting distinctive environments that reflect clarity, character, and intent—delivering a consistent standard of excellence across residential and commercial projects.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 bg-stone-100" data-testid="story-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5"
            >
              <h2 className="font-manrope text-3xl md:text-4xl font-light tracking-tight text-stone-900">
                Creating
                <br />
                <span className="italic">timeless spaces</span>
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:col-span-7 space-y-6"
            >
              <p className="font-dm-sans text-stone-600 leading-relaxed">
                BIOM curates bespoke environments where form, function and emotion exist in seamless harmony. Every project is treated as a tailored journey, shaped through deep collaboration and a refined design process that translates each client's aspirations into spaces that are sophisticated, purposeful, and timeless.
              </p>
              <p className="font-dm-sans text-stone-600 leading-relaxed">
                From private residences to commercial destinations, each creation reflects a commitment to precision, individuality and quiet luxury. Rooted in traditional craftsmanship and elevated by contemporary innovation, our spaces honour their context while looking confidently towards the future.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="font-manrope text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-6">
            Ready to begin?
          </h2>
          <p className="font-dm-sans text-stone-500 leading-relaxed mb-10">
            We'd love to learn about your project and explore how we can bring your vision to life.
          </p>
          <Link
            to="/contact"
            data-testid="about-contact-cta"
            className="inline-flex items-center gap-3 rounded-full px-10 py-4 text-xs uppercase tracking-widest font-manrope font-medium bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-300"
          >
            Get in Touch
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
